import { ChangeEvent } from "react"
import { useTranslation } from "react-i18next"

import { Input } from "@/components/Form/Input"
import { RefreshIcon } from "@/components/icons/RefreshIcon"
import { join } from "@/utils/join"

import { useTileCurrencyPair } from "../TileContext"
import { QuoteStateStage, useRfqState } from "../TileFooter/RfqTimer"
import {
  onChangeNotionalValue,
  useDefaultNotional,
  useIsNotionalValid,
  useNotional,
} from "./notional.state"

type Props = {
  id: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  base: string
  valid: boolean
  disabled: boolean
  canReset: boolean
  onReset: () => void
}

export const NotionalInputInner = ({
  id,
  base,
  valid,
  disabled,
  value,
  onChange,
  canReset,
  onReset,
}: Props) => {
  const { t } = useTranslation()
  return (
    <div className="z-10 flex flex-1 items-center justify-center gap-xs">
      <label className="text-sm-regular text-tertiary" htmlFor={id}>
        {base}
      </label>
      <Input
        id={id}
        className="w-[83px]! bg-secondary text-center"
        valid={valid}
        disabled={disabled}
        value={value}
        onChange={onChange}
        onFocus={(event) => {
          event.target.select()
        }}
      />
      <button
        className={join(
          canReset
            ? "cursor-pointer bg-brand-solid text-utility-gray-700 hover:text-primary dark:tooltip-supporting-text"
            : "hidden",
          "rounded-xs p-xs",
        )}
        onClick={onReset}
      >
        <RefreshIcon width={12} height={12} />
      </button>
      {!valid && (
        <p className="ml-6 text-xs-regular leading-1 text-error-primary">
          {t("liveRates.notionalInput.maxExceeded")}
        </p>
      )}
    </div>
  )
}

export const NotionalInput = () => {
  const { base, symbol } = useTileCurrencyPair()
  const defaultNotional = useDefaultNotional()
  const [value, inputValue] = useNotional()
  const valid = useIsNotionalValid()
  const { stage: quoteStage } = useRfqState()
  const id = `notional-input-${symbol}`

  return (
    <NotionalInputInner
      id={id}
      base={base}
      valid={valid}
      disabled={[QuoteStateStage.Received, QuoteStateStage.Requested].includes(
        quoteStage,
      )}
      value={inputValue}
      onChange={(e) => {
        onChangeNotionalValue(symbol, e.target.value)
      }}
      canReset={value !== defaultNotional}
      onReset={() => {
        onChangeNotionalValue(symbol, defaultNotional.toString(10))
      }}
    />
  )
}
